import { CarePlanSignatureTask } from '@seen/core/models/tasks/signature/carePlanSignatureTask'
import { SDRSignatureTask } from '@seen/core/models/tasks/signature/sdrSignatureTask'
import { DocumentReviewTask } from '@seen/core/models/tasks/standard/documentReviewTask'
import { QueuedEditsReviewTask } from '@seen/core/models/tasks/standard/queuedEditsReviewTask'
import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { isOfType } from '@seen/core/utils'
import { truncate } from 'lodash-es'
import { extractTextFromHTML } from '#src/utils/html.ts'

/* Resource type label used as a fallback when the task title isn't descriptive */
function focusTypeLabel(task: StandardTask): string {
  if (isOfType(task.focus, 'ServiceRequest')) {
    return 'Order'
  }
  if (isOfType(task.focus, 'CarePlan')) {
    return 'Care Plan'
  }
  if (isOfType(task.focus, 'DocumentReference')) {
    return 'Document'
  }
  if (isOfType(task.focus, 'Task')) {
    return 'SDR'
  }
  return 'Task'
}

/* Known generic task type names that don't carry useful context on their own */
const GENERIC_TASK_NAMES = new Set([
  'Document Review',
  'Care Plan Signature',
  'Care Plan Participant Signature',
  'SDR Signature',
  'Note Signature',
  'Order Signature',
  'Task',
  'Order',
  'Care Plan',
  'Document',
  'SDR',
])

/* Derive a human-readable title for the sidebar header.
 * Prefers the task's own title when it contains more than just the type name
 * (e.g. "Order Signature: Physical Therapy Referral").
 * Falls back to a generic resource type label otherwise. */
export function taskFocusTitle(task: StandardTask): string {
  const rawTitle = extractTextFromHTML(task.title)
  const fallback = focusTypeLabel(task)

  if (!rawTitle) {
    return fallback
  }

  /* If the title has a "Type: Description" shape, it's descriptive -- use it */
  if (rawTitle.includes(':')) {
    return rawTitle
  }

  /* If the title equals a known generic type name, fall back */
  if (GENERIC_TASK_NAMES.has(rawTitle)) {
    return fallback
  }

  return rawTitle
}

interface TaskSidebarHeaderPropsResult {
  actionUrl?: string
  actionLabel?: string
  contextUrl?: string
  contextLabel?: string
}

export function taskSidebarHeaderProps(
  task: StandardTask
): TaskSidebarHeaderPropsResult {
  const taskUrl = `/tasks/${task.id}`
  const chartUrl = task.actionUrl !== taskUrl ? task.actionUrl : undefined
  const hasInlineFocus = hasInlineFocusContent(task)
  return {
    actionUrl: chartUrl,
    actionLabel: 'Open chart',
    contextUrl: hasInlineFocus ? taskUrl : undefined,
    contextLabel: hasInlineFocus ? 'See task' : undefined,
  }
}

/* True when TaskFocusContent renders a specialized card (not the generic fallback) */
function hasInlineFocusContent(task: StandardTask): boolean {
  const fhirTask = task.toFhirResource()
  return (
    isOfType(task.focus, 'ServiceRequest') ||
    (isOfType(task.focus, 'CarePlan') &&
      CarePlanSignatureTask.isInstance(fhirTask)) ||
    (isOfType(task.focus, 'Task') && SDRSignatureTask.isInstance(fhirTask)) ||
    DocumentReviewTask.isInstance(fhirTask) ||
    QueuedEditsReviewTask.isInstance(fhirTask)
  )
}

export function snippetText(text: string | undefined): string | undefined {
  if (!text) {
    return undefined
  }
  const plain = extractTextFromHTML(text)
  if (!plain) {
    return undefined
  }
  return truncate(plain, { length: 50 })
}
